/* ============================================================
   BBW · Cart Drawer controller
   Talks to Shopify's AJAX Cart (/cart/add.js, /cart/change.js)
   and refreshes the drawer via the Section Rendering API.

   Sync model (fixes raciness, slow removes, image flashing):
   - SEQUENCING  every request takes a token; only the latest
     response may touch the DOM, so a stale render can never
     resurrect a removed item.
   - MORPHING    server HTML is reconciled per line-item key —
     existing item nodes (and their <img>s) are reused, only
     changed text is patched → no flash on add/resync.
   - OPTIMISTIC  qty/remove update the row instantly; requests
     are serialized per line and rapid clicks collapse into a
     single request carrying the final quantity.
   - RESYNC      after any change, re-fetch at 650/1400/2600 ms
     so server-side gift/discount apps settle; a resync that
     brings identical content is a no-op (morph diffs nothing).
   ============================================================ */
(function () {
  var SECTION = 'bbw-cart-drawer';

  var seq = 0;              // response-ordering token
  var resyncTimers = [];
  var lines = {};           // key -> { desired, inFlight, sendTimer }
  var byobSessionClearPromise = null;

  function root() { return document.querySelector('[data-bbw-cart]'); }
  function contentEl() { var r = root(); return r ? r.querySelector('[data-cart-content]') : null; }

  function open() {
    var r = root();
    if (!r) return;
    r.classList.add('is-open');
    r.setAttribute('aria-hidden', 'false');
    document.body.classList.add('bbw-cart-lock');
    applyUpsell();
    syncRailBars();
    syncWallets();
  }
  function close() {
    var r = root();
    if (!r) return;
    r.classList.remove('is-open');
    r.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('bbw-cart-lock');
  }

  function updateCount(n) {
    if (n === undefined || n === null) return;
    document.querySelectorAll('[data-cart-count]').forEach(function (el) {
      el.textContent = n;
    });
  }

  function isHiddenCartLine(item) {
    var props = item && item.properties ? item.properties : {};
    return props._instant_hidden === 'true' || props['_instant-hidden'] === 'true';
  }

  function isByobCartLine(item) {
    var props = item && item.properties ? item.properties : {};
    return props._byob_item === 'true' || props._byob_slot != null || props._byob_gift === 'true';
  }

  function byobGiftLineUpdates(cart) {
    var updates = {};
    ((cart && cart.items) || []).forEach(function (item) {
      var props = item && item.properties ? item.properties : {};
      if (props._byob_gift === 'true' && item.key) updates[item.key] = 0;
    });
    return updates;
  }

  function cartNeedsByobSessionClear(cart) {
    var attrs = cart && cart.attributes ? cart.attributes : {};
    if (String(attrs.byob_session || '') !== 'true') return false;
    return ((cart && cart.items) || []).some(function (item) {
      return !isHiddenCartLine(item) && !isByobCartLine(item);
    });
  }

  function clearByobSessionIfMixedCart(cart) {
    if (!cartNeedsByobSessionClear(cart)) return Promise.resolve(cart);
    if (byobSessionClearPromise) return byobSessionClearPromise;
    var updates = byobGiftLineUpdates(cart);
    var body = {
      attributes: {
        byob_session: '',
        byob_initiated_at: ''
      },
      sections: SECTION,
      sections_url: window.location.pathname
    };
    if (Object.keys(updates).length) body.updates = updates;

    byobSessionClearPromise = fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Accept': 'application/json', 'Content-Type': 'application/json' },
      body: JSON.stringify(body)
    })
      .then(function (r) {
        if (!r.ok) throw new Error('BYOB session clear failed: ' + r.status);
        return r.json();
      })
      .then(function (updatedCart) {
        if (updatedCart && updatedCart.sections) applySections(updatedCart.sections);
        return updatedCart;
      })
      .catch(function (err) {
        console.warn('[bbw-cart] BYOB session clear failed', err);
        return cart;
      })
      .finally(function () {
        byobSessionClearPromise = null;
      });

    return byobSessionClearPromise;
  }

  function continueToCheckoutAfterByobCheck(href) {
    var destination = href || '/checkout';
    fetch('/cart.js')
      .then(function (r) { return r.json(); })
      .then(clearByobSessionIfMixedCart)
      .catch(function () {})
      .finally(function () {
        window.location.href = destination;
      });
  }

  function hasPendingOps() {
    for (var k in lines) if (lines[k] && (lines[k].inFlight || lines[k].sendTimer)) return true;
    return false;
  }

  // Tell the rest of the ecosystem (gift/discount apps, legacy listeners) that
  // the cart changed — the legacy cart did this too. Tagged so our own
  // cart:update listener can ignore it (no self-loop).
  function announce() {
    try {
      document.dispatchEvent(new CustomEvent('cart:update', { bubbles: true, detail: { source: 'bbw-cart' } }));
    } catch (e) { /* older browsers: skip */ }
  }

  /* ── Morph: reconcile server HTML into the live drawer ─────
     Item nodes are keyed on data-key and REUSED: unchanged rows
     aren't touched at all; changed rows update their body markup
     while media <img> is never recreated.
     Non-item regions (header, footer, empty state) are small and
     imageless, so they're replaced wholesale when they differ. */
  function patchItem(oldItem, newItem) {
    if (oldItem.outerHTML === newItem.outerHTML) return;
    var oldBody = oldItem.querySelector('.bbw-cart__item-body');
    var newBody = newItem.querySelector('.bbw-cart__item-body');
    if (oldBody && newBody) {
      if (oldBody.outerHTML !== newBody.outerHTML) oldBody.outerHTML = newBody.outerHTML;
      return;
    }

    var pairs = [
      ['.bbw-cart__qty-num', 'text'],
      ['.bbw-cart__item-price', 'html'],
      ['.bbw-cart__item-info', 'html'],
      ['.bbw-cart__gift-badge', 'presence']
    ];
    pairs.forEach(function (p) {
      var o = oldItem.querySelector(p[0]);
      var n = newItem.querySelector(p[0]);
      if (p[1] === 'presence') {
        if (o && !n) o.parentNode.removeChild(o);
        else if (!o && n) {
          var top = oldItem.querySelector('.bbw-cart__item-top');
          if (top) top.appendChild(n.cloneNode(true));
        }
        return;
      }
      if (!o || !n) return;
      if (p[1] === 'text') { if (o.textContent !== n.textContent) o.textContent = n.textContent; }
      else { if (o.innerHTML !== n.innerHTML) o.innerHTML = n.innerHTML; }
    });
  }

  function morph(html) {
    var cur = contentEl();
    if (!cur || !html) return;
    var parsed = new DOMParser().parseFromString(html, 'text/html');
    var next = parsed.querySelector('[data-cart-content]');
    if (!next) return;
    var fw = parsed.querySelector('[data-bbw-wallets] shopify-accelerated-checkout-cart');
    if (fw) fetchedWalletComp = fw;

    var curList = cur.querySelector('.bbw-cart__items');
    var nextList = next.querySelector('.bbw-cart__items');

    // Structure changed (filled <-> empty): replace everything once.
    if (!curList || !nextList) {
      if (cur.innerHTML !== next.innerHTML) cur.innerHTML = next.innerHTML;
      syncWalletFlags(cur, next);
      // The replacement HTML carries the SERVER's default data-layout, so the
      // assigned variant has to be re-stamped here too. Without this an
      // empty-to-filled resync drops the shopper back to the control layout.
      applyUpsell();
      syncRailBars();
      return;
    }

    // 1) Items — keyed reconciliation.
    var oldByKey = {};
    curList.querySelectorAll('[data-cart-item]').forEach(function (el) {
      oldByKey[el.getAttribute('data-key')] = el;
    });
    var newItems = nextList.querySelectorAll('[data-cart-item]');
    var cursor = null; // last placed node, for ordering
    newItems.forEach(function (newItem) {
      var key = newItem.getAttribute('data-key');
      var node = oldByKey[key];
      if (node) {
        patchItem(node, newItem);
        node.classList.remove('is-removing');
        delete oldByKey[key];
      } else {
        node = newItem.cloneNode(true);
      }
      // Ensure order matches server order without re-inserting settled nodes.
      var expectedPrev = cursor;
      var actualPrev = node.previousElementSibling;
      if (node.parentNode !== curList || actualPrev !== expectedPrev) {
        if (expectedPrev && expectedPrev.nextSibling) curList.insertBefore(node, expectedPrev.nextSibling);
        else if (expectedPrev) curList.appendChild(node);
        else curList.insertBefore(node, curList.firstChild);
      }
      cursor = node;
    });
    // Remove rows the server no longer has.
    for (var k in oldByKey) {
      if (oldByKey[k].parentNode) oldByKey[k].parentNode.removeChild(oldByKey[k]);
    }

    // 2) Header + footer + anything else: swap only when different.
    ['.bbw-cart__header', '.bbw-cart__footer'].forEach(function (sel) {
      var o = cur.querySelector(sel);
      var n = next.querySelector(sel);
      if (o && n && o.innerHTML !== n.innerHTML) o.innerHTML = n.innerHTML;
    });

    // 3) Reward bar: patch in place (state attr, text, fill width) so the CSS
    //    width transition animates instead of the node being recreated.
    var oRw = cur.querySelector('.bbw-cart__reward');
    var nRw = next.querySelector('.bbw-cart__reward');
    if (oRw && !nRw) {
      oRw.parentNode.removeChild(oRw);
    } else if (!oRw && nRw) {
      var hdr = cur.querySelector('.bbw-cart__header');
      if (hdr) hdr.insertAdjacentElement('afterend', nRw.cloneNode(true));
    } else if (oRw && nRw) {
      oRw.setAttribute('data-reward-state', nRw.getAttribute('data-reward-state') || '');
      var oTxt = oRw.querySelector('.bbw-cart__reward-text');
      var nTxt = nRw.querySelector('.bbw-cart__reward-text');
      if (oTxt && nTxt && oTxt.outerHTML !== nTxt.outerHTML) oTxt.outerHTML = nTxt.outerHTML;
      var oFill = oRw.querySelector('.bbw-cart__reward-fill');
      var nFill = nRw.querySelector('.bbw-cart__reward-fill');
      if (oFill && nFill && oFill.style.width !== nFill.style.width) oFill.style.width = nFill.style.width;
    }

    // 4) Upsell rail: swap contents when they differ (a just-added product
    //    drops out via the server-side dedupe); handle presence changes by
    //    anchoring to the items list.
    var oUp = cur.querySelector('.bbw-cart__upsells');
    var nUp = next.querySelector('.bbw-cart__upsells');
    if (oUp && !nUp) {
      oUp.parentNode.removeChild(oUp);
    } else if (!oUp && nUp) {
      var itemsEl = cur.querySelector('.bbw-cart__items');
      if (itemsEl) itemsEl.insertAdjacentElement('afterend', nUp.cloneNode(true));
    } else if (oUp && nUp && oUp.innerHTML !== nUp.innerHTML) {
      oUp.innerHTML = nUp.innerHTML;
    }
    syncWalletFlags(cur, next);
    // A fresh block arrives holding the server's data-layout, so re-stamp the
    // active variant before measuring the rail.
    applyUpsell();
    syncRailBars();
  }

  /* ── Express wallets: the container lives OUTSIDE [data-cart-content] so
     its Shopify-injected iframes are never destroyed by a morph. The fresh
     render's data-cart-* flags are copied onto the live content node, then
     visibility is just a [hidden] toggle — the wallet reads the live cart
     at tap time, so its content never needs re-rendering. */
  function syncWalletFlags(cur, next) {
    if (next) {
      cur.setAttribute('data-cart-items', next.getAttribute('data-cart-items') || '0');
      cur.setAttribute('data-cart-byob', next.getAttribute('data-cart-byob') || 'false');
    }
    syncWallets();
  }
  /* Shopify's wallet script boots the component against the cart cookie as
     of page load — a page that loaded with an EMPTY cart has no cookie, the
     boot bails, and the dead instance may carry stamped state attributes.
     First time the cart gains items on such a page, swap in the PRISTINE
     component from the latest fetched section render (re-filtered through
     the inline allowlist hook) so it boots clean against the now-live cart.
     null = not yet measured on this page. */
  var walletsBornEmpty = null;
  var fetchedWalletComp = null;
  function reinitWalletComponent(wrap) {
    var comp = wrap.querySelector('shopify-accelerated-checkout-cart');
    if (!comp || !comp.parentNode) return;
    var fresh;
    if (fetchedWalletComp) {
      fresh = document.importNode(fetchedWalletComp, true);
      if (window.__bbwFilterWalletComp && !window.__bbwFilterWalletComp(fresh)) return;
    } else {
      fresh = document.createElement('shopify-accelerated-checkout-cart');
      for (var i = 0; i < comp.attributes.length; i++) {
        if (comp.attributes[i].name === 'disabled') continue;
        fresh.setAttribute(comp.attributes[i].name, comp.attributes[i].value);
      }
    }
    comp.parentNode.replaceChild(fresh, comp);
  }
  function syncWallets() {
    var r = root();
    if (!r) return;
    var content = contentEl();
    if (!content) return;
    var items = parseInt(content.getAttribute('data-cart-items') || '0', 10);
    var byob = content.getAttribute('data-cart-byob') === 'true';
    if (walletsBornEmpty === null) walletsBornEmpty = items <= 0;
    var wallets = r.querySelector('[data-bbw-wallets]');
    if (wallets) {
      wallets.hidden = items <= 0 || byob;
      if (!wallets.hidden && walletsBornEmpty) {
        walletsBornEmpty = false;
        reinitWalletComponent(wallets);
      }
    }
    // Guarantee line (lives outside the morph zone with the wallets so it can
    // sit BELOW them): items-only gate — stays visible during BYOB sessions.
    var post = r.querySelector('[data-bbw-postfooter]');
    if (post) post.hidden = items <= 0;
  }

  function applySections(sections) {
    if (sections && sections[SECTION]) morph(sections[SECTION]);
  }

  /* ── Re-sync: let server-side gift/discount apps settle ────
     Applies only if it is still the newest request AND no user
     edit is mid-flight (never stomp optimistic state). */
  function resyncOnce() {
    if (hasPendingOps()) { scheduleResync(); return; }
    var my = ++seq;
    fetch('/?sections=' + SECTION)
      .then(function (r) { return r.json(); })
      .then(function (sections) { if (my === seq && !hasPendingOps()) applySections(sections); })
      .then(function () { return fetch('/cart.js'); })
      .then(function (r) { return r.json(); })
      .then(clearByobSessionIfMixedCart)
      .then(function (cart) { if (my === seq && cart) updateCount(cart.item_count); })
      .catch(function () {});
  }
  function scheduleResync() {
    resyncTimers.forEach(clearTimeout);
    resyncTimers = [650, 1400, 2600].map(function (delay) {
      return window.setTimeout(resyncOnce, delay);
    });
  }

  function suppressCartEditAtc(reason) {
    if (window.basedAtcAnalyticsGuard && typeof window.basedAtcAnalyticsGuard.suppressAddToCart === 'function') {
      window.basedAtcAnalyticsGuard.suppressAddToCart(reason, 4000);
    }
  }

  function dedupeCartAddAtc(reason) {
    if (window.basedAtcAnalyticsGuard && typeof window.basedAtcAnalyticsGuard.dedupeAddToCart === 'function') {
      window.basedAtcAnalyticsGuard.dedupeAddToCart(reason, 2500);
    }
  }

  /* ── Quantity changes: optimistic, serialized per line ───── */
  function itemEl(key) {
    var r = root();
    return r ? r.querySelector('[data-cart-item][data-key="' + key + '"]') : null;
  }

  function renderOptimistic(key) {
    var st = lines[key];
    var el = itemEl(key);
    if (!st || !el) return;
    var num = el.querySelector('.bbw-cart__qty-num');
    if (num) num.textContent = st.desired;
    el.classList.toggle('is-removing', st.desired === 0);
    el.classList.add('is-busy');
  }

  function sendLine(key) {
    var st = lines[key];
    if (!st || st.inFlight) return;
    st.inFlight = true;
    var sending = st.desired;
    var my = ++seq;
    suppressCartEditAtc('bbw-cart:quantity-change');
    fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({
        id: key,
        quantity: sending,
        sections: SECTION,
        sections_url: window.location.pathname
      })
    })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        st.inFlight = false;
        if (st.desired !== sending) { sendLine(key); return; } // user kept clicking — send final value
        delete lines[key];
        var el = itemEl(key);
        if (el) el.classList.remove('is-busy');
        if (my === seq) {
          if (cart && cart.sections) applySections(cart.sections);
          if (cart) updateCount(cart.item_count);
        }
        announce();
        scheduleResync();
      })
      .catch(function () {
        st.inFlight = false;
        delete lines[key];
        resyncOnce(); // restore truth from server
      });
  }

  function queueChange(key, delta, absolute) {
    var st = lines[key];
    if (!st) {
      var el = itemEl(key);
      var num = el ? el.querySelector('.bbw-cart__qty-num') : null;
      var current = num ? parseInt(num.textContent, 10) || 0 : 0;
      st = lines[key] = { desired: current, inFlight: false, sendTimer: null };
    }
    st.desired = absolute !== undefined ? absolute : Math.max(0, st.desired + delta);
    renderOptimistic(key);
    // Collapse rapid clicks into one request; removals send immediately.
    if (st.sendTimer) clearTimeout(st.sendTimer);
    var wait = st.desired === 0 ? 0 : 220;
    st.sendTimer = window.setTimeout(function () {
      st.sendTimer = null;
      sendLine(key);
    }, wait);
  }

  /* ── Add to cart: intercept the redesign ATC forms ─────────
     Capture phase + stopImmediatePropagation so we win over ANY
     theme handler that might let the form post natively. */
  document.addEventListener('submit', function (e) {
    var form = e.target.closest('form[data-bbw-add]');
    if (!form) return;
    e.preventDefault();
    e.stopImmediatePropagation();

    var btn = form.querySelector('button[type="submit"]');
    if (btn && btn.disabled) return;
    var label = btn ? btn.querySelector('.bbw-atc__label') : null;
    var prevLabel = label ? label.textContent : '';
    var fd = new FormData(form);
    fd.append('sections', SECTION);
    fd.append('sections_url', window.location.pathname);
    if (btn) {
      btn.classList.add('is-loading');
      btn.disabled = true;
      btn.setAttribute('aria-busy', 'true');
    }
    if (label) label.textContent = 'ADDING…';

    /* Snapshot the layout the shopper actually SAW, at click time, from the
       block this form sits in. Reading the global after the request resolves
       reports null whenever the upsell runs without the A/B (nothing sets the
       document attribute then), and misattributes the conversion if the cell
       changes while the add is in flight. Validated through upsellLayout so a
       hand-edited attribute cannot reach analytics. */
    var upsellBlock = form.closest ? form.closest('.bbw-cart__upsells') : null;
    var addedFromLayout = upsellBlock ? upsellLayout(upsellBlock.getAttribute('data-layout')) : null;

    var my = ++seq;
    dedupeCartAddAtc('bbw-cart:add');
    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Accept': 'application/json' },
      body: fd
    })
      .then(function (r) { return r.json().then(function (d) { return { ok: r.ok, data: d }; }); })
      .then(function (res) {
        if (!res.ok || res.data.status) return; // e.g. sold out / over stock
        /* Conversion signal for the upsell A/B test: fires only for the cart
           upsell forms and carries the layout the shopper was shown. */
        if (form.hasAttribute('data-upsell-add')) {
          try {
            document.dispatchEvent(new CustomEvent('bbw:cart-upsell-add', {
              detail: {
                productId: form.getAttribute('data-product-id'),
                variantId: form.getAttribute('data-variant-id'),
                layout: addedFromLayout
              }
            }));
          } catch (e) {}
        }
        if (my === seq && res.data.sections) applySections(res.data.sections);
        open();
        announce();
        scheduleResync();
        return fetch('/cart.js').then(function (r) { return r.json(); }).then(function (cart) {
          return clearByobSessionIfMixedCart(cart);
        }).then(function (cart) {
          if (my === seq && cart) updateCount(cart.item_count);
        });
      })
      .catch(function () {})
      .finally(function () {
        if (label && prevLabel) label.textContent = prevLabel;
        if (btn) {
          btn.classList.remove('is-loading');
          btn.disabled = false;
          btn.removeAttribute('aria-busy');
        }
      });
  }, true);

  /* ── Delegated clicks (open / close / qty / remove) ──────── */
  document.addEventListener('click', function (e) {
    var checkout = e.target.closest && e.target.closest('.bbw-cart__checkout, .cart__checkout-button, button[name="checkout"]');
    if (checkout && !checkout.closest('[data-byob-checkout]')) {
      e.preventDefault();
      continueToCheckoutAfterByobCheck('/checkout');
      return;
    }

    if (e.target.closest('[data-cart-open]')) {
      e.preventDefault();
      open();
      return;
    }

    var r = root();
    if (!r || !r.contains(e.target)) return;

    if (e.target.closest('[data-cart-close]')) { e.preventDefault(); close(); return; }

    var inc = e.target.closest('[data-qty-inc]');
    var dec = e.target.closest('[data-qty-dec]');
    var rem = e.target.closest('[data-cart-remove]');
    if (!inc && !dec && !rem) return;

    e.preventDefault();
    var trigger = inc || dec || rem;
    var key = trigger.getAttribute('data-key');

    if (rem) queueChange(key, 0, 0);
    else if (inc) queueChange(key, 1);
    else if (dec) queueChange(key, -1);
  });

  /* ── Legacy pages: react to the theme's cart:update event ──
     Legacy ATC flows (Horizon product-form, quick-add, sticky
     CTA…) dispatch document-level 'cart:update' after adding.
     We refresh + open; sequencing prevents double-render races
     with our own add path. */
  var refreshing = false;
  function refreshAndOpen() {
    if (refreshing) return;
    refreshing = true;
    var my = ++seq;
    fetch('/?sections=' + SECTION)
      .then(function (r) { return r.json(); })
      .then(function (sections) { if (my === seq) applySections(sections); })
      .catch(function () {})
      .finally(function () {
        open();
        fetch('/cart.js')
          .then(function (r) { return r.json(); })
          .then(clearByobSessionIfMixedCart)
          .then(function (cart) { if (cart) updateCount(cart.item_count); })
          .catch(function () {})
          .finally(function () { refreshing = false; });
      });
  }
  document.addEventListener('cart:update', function (e) {
    if (e.detail && e.detail.source === 'bbw-cart') return; // our own announcement
    refreshAndOpen();
    scheduleResync();
  });

  /* ── Escape closes ────────────────────────────────────────── */
  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape') close();
  });

  /* ── Upsell A/B activation (external test tool) ─────────────
     With the "A/B hold" theme setting on, the section renders the
     upsell block hidden (data-upsell-hold="1") and this hook is
     what reveals it, so a tool like Coframe/Jarvis owns who sees
     it and in which layout:

       window.BBWCartUpsell.enable('vertical' | 'horizontal')
       window.BBWCartUpsell.disable()
       window.BBWCartUpsell.state()   -> 'rows' | 'rail' | null

     A single attribute on <html> carries the choice, which is what
     makes a variant survive drawer morphs, re-opens and section
     re-renders — re-applied below wherever the drawer is rebuilt.
     A tool that injects BEFORE this file loads can set
     window.__bbwCartUpsellAB = 'vertical' instead; it is read at
     boot. The choice is kept in sessionStorage so a visitor holds
     their variant across page views even if one page misses the
     injection, which keeps the cells clean. */
  var UPSELL_ALIASES = {
    vertical: 'rows', rows: 'rows', list: 'rows', stacked: 'rows',
    horizontal: 'rail', rail: 'rail', cards: 'rail', carousel: 'rail'
  };
  var UPSELL_STORE_KEY = 'bbw:cart-upsell-ab';

  function upsellLayout(value) {
    var key = String(value == null ? '' : value).trim().toLowerCase();
    /* Own-property lookup only. A plain object inherits `__proto__`,
       `constructor` and friends, so an injected value matching one of those
       would resolve to an object instead of falling through to null, and that
       object would then be stringified into the layout attribute, session
       storage and the analytics payload. */
    return Object.prototype.hasOwnProperty.call(UPSELL_ALIASES, key) ? UPSELL_ALIASES[key] : null;
  }
  function rememberUpsell(layout) {
    try {
      if (layout) window.sessionStorage.setItem(UPSELL_STORE_KEY, layout);
      else window.sessionStorage.removeItem(UPSELL_STORE_KEY);
    } catch (e) {}
  }
  function rememberedUpsell() {
    try { return window.sessionStorage.getItem(UPSELL_STORE_KEY); } catch (e) { return null; }
  }
  /* Stamps the active layout onto every held block. data-layout is the same
     attribute the theme setting writes, so a variant renders through the
     production rules rather than a test-only style. */
  function applyUpsell() {
    var layout = upsellLayout(document.documentElement.getAttribute('data-bbw-upsell'));
    if (!layout) return;
    var held = document.querySelectorAll('.bbw-cart__upsells[data-upsell-hold="1"]');
    for (var i = 0; i < held.length; i++) held[i].setAttribute('data-layout', layout);
    syncRailBars();
  }
  function setUpsell(value, deactivate) {
    var previous = upsellLayout(document.documentElement.getAttribute('data-bbw-upsell'));
    var layout = upsellLayout(value);
    /* Invalid external values are rejected without changing an existing
       assignment. Only the public disable() path may deliberately clear a
       shopper's cell, persistence and DOM state. */
    if (!layout && !deactivate) return null;
    if (layout) document.documentElement.setAttribute('data-bbw-upsell', layout);
    else document.documentElement.removeAttribute('data-bbw-upsell');
    rememberUpsell(layout);
    applyUpsell();
    /* Exposure fires ONLY on a real transition into a cell. An unassigned boot
       (layout null), a repeat call naming the cell the shopper is already in,
       and disable() all stay silent — otherwise control traffic and ordinary
       re-renders would be recorded as exposures and the test would be reading
       its own noise. Deactivation gets a distinct event so it can still be
       tracked without being mistaken for exposure. */
    if (layout && layout !== previous) {
      try {
        document.dispatchEvent(new CustomEvent('bbw:cart-upsell', { detail: { layout: layout } }));
      } catch (e) {}
    } else if (!layout && previous) {
      try {
        document.dispatchEvent(new CustomEvent('bbw:cart-upsell-off', { detail: { previous: previous } }));
      } catch (e) {}
    }
    return layout;
  }
  window.BBWCartUpsell = {
    enable: function (value) { return setUpsell(value == null ? 'vertical' : value); },
    disable: function () { return setUpsell(null, true); },
    state: function () { return upsellLayout(document.documentElement.getAttribute('data-bbw-upsell')); },
    apply: applyUpsell
  };

  /* ── Upsell rail (data-layout="rail"): desktop scrolling ────
     The strip is mouse-draggable on desktop; a drag swallows the
     click it would otherwise fire, so ADD/links only trigger on
     a true click. The bottom stroke doubles as a gold scroll
     progress bar, synced here. Delegated — survives morphs. */
  function railList(target) {
    var list = target && target.closest ? target.closest('.bbw-cart__upsells-list') : null;
    if (!list) return null;
    var wrap = list.closest('.bbw-cart__upsells');
    return wrap && wrap.getAttribute('data-layout') === 'rail' ? list : null;
  }

  function syncRailBar(list) {
    var wrap = list.closest('.bbw-cart__upsells');
    var thumb = wrap ? wrap.querySelector('[data-rail-thumb]') : null;
    if (!thumb) return;
    var sw = list.scrollWidth;
    var cw = list.clientWidth;
    if (sw <= cw) {
      thumb.style.width = '100%';
      thumb.style.left = '0';
      return;
    }
    thumb.style.width = (cw / sw * 100) + '%';
    thumb.style.left = (list.scrollLeft / sw * 100) + '%';
  }
  function syncRailBars() {
    var r = root();
    if (!r) return;
    r.querySelectorAll('.bbw-cart__upsells[data-layout="rail"] .bbw-cart__upsells-list').forEach(syncRailBar);
  }
  // scroll doesn't bubble — capture catches it from any rail list.
  document.addEventListener('scroll', function (e) {
    var list = railList(e.target);
    if (list) syncRailBar(list);
  }, true);

  var railDrag = null;
  document.addEventListener('dragstart', function (e) {
    if (railList(e.target)) e.preventDefault(); // no native image/link ghosts
  });
  document.addEventListener('mousedown', function (e) {
    railDrag = null; // clear any stale drag (mouseup can land outside the window)
    var list = railList(e.target);
    if (!list || e.button !== 0) return;
    railDrag = { list: list, startX: e.clientX, startLeft: list.scrollLeft, moved: false };
  });
  document.addEventListener('mousemove', function (e) {
    if (!railDrag) return;
    if (!(e.buttons & 1)) {
      // Button was released outside the window — abort, never swallow a click.
      railDrag.list.classList.remove('is-dragging');
      railDrag = null;
      return;
    }
    var dx = e.clientX - railDrag.startX;
    if (!railDrag.moved && Math.abs(dx) > 4) railDrag.moved = true;
    if (railDrag.moved) {
      railDrag.list.scrollLeft = railDrag.startLeft - dx;
      railDrag.list.classList.add('is-dragging');
      e.preventDefault();
    }
  });
  document.addEventListener('mouseup', function () {
    if (!railDrag) return;
    var was = railDrag;
    railDrag = null;
    was.list.classList.remove('is-dragging');
    if (was.moved) {
      var swallow = function (ev) {
        ev.preventDefault();
        ev.stopPropagation();
      };
      document.addEventListener('click', swallow, true);
      setTimeout(function () {
        document.removeEventListener('click', swallow, true);
      }, 0);
    }
  });

  /* Back/forward-cache restores resurrect the pre-navigation DOM, so
     data-cart-items / data-cart-byob can be stale against the live cart —
     a BYOB session started since would show wallets that bypass its
     checkout-URL discounts. Restore truth from the server. */
  window.addEventListener('pageshow', function (e) {
    if (e.persisted) resyncOnce();
  });

  syncRailBars();
  syncWallets();
  /* A valid explicit global (a tool that injected before this file) beats the
     remembered session value. Validate it before applying precedence so a
     malformed injection cannot suppress a shopper's valid saved cell. */
  var injectedUpsell = upsellLayout(window.__bbwCartUpsellAB);
  var savedUpsell = upsellLayout(rememberedUpsell());
  setUpsell(injectedUpsell || savedUpsell);
})();
